import { type ComponentProps, useRef, useState } from 'react';

import { DateHelpers, DISPLAY_DATE_FORMAT } from '@crm/shared/lib';
import { DatePicker } from '@crm/shared/ui';

import type { FieldValue } from '../../model/informationBlockFields';

type EditableDateFieldProps = Omit<
    ComponentProps<typeof DatePicker>,
    'value' | 'onChange' | 'label'
> & {
    value: FieldValue;
    onCommit: (nextValue: string) => void;
    onCancel: () => void;
};

export const EditableDateField = ({
    placeholder = '-',
    value,
    onCommit,
    onCancel,
    ...props
}: EditableDateFieldProps) => {
    const initialValue = String(value ?? '');
    const [draftValue, setDraftValue] = useState(initialValue);
    const draftValueRef = useRef(initialValue);

    const updateDraftValue = (nextValue: string) => {
        draftValueRef.current = nextValue;
        setDraftValue(nextValue);
    };

    return (
        <DatePicker
            {...props}
            hideLabel
            normalizeOnBlur
            value={draftValue}
            placeholder={placeholder}
            parseDate={DateHelpers.parseDisplayDate}
            formatDate={DateHelpers.formatDisplayDate}
            fieldClassName="w-full min-w-0"
            inputGroupClassName="-my-1.5 h-7 w-full min-w-0 rounded-lg border border-transparent bg-transparent transition-colors hover:border-gray-200 hover:bg-white has-[[data-slot=input-group-control]:focus-visible]:border-gray-200 has-[[data-slot=input-group-control]:focus-visible]:bg-white has-[[data-slot=input-group-control]:focus-visible]:shadow-sm has-[[data-slot=input-group-control]:focus-visible]:ring-0 [&_[data-slot=input-group-control]]:!text-xs [&_[data-slot=input-group-control]]:!leading-4 [&_[data-slot=input-group-control]]:!font-normal"
            className="h-7 px-2 py-1 !text-xs !leading-4 !font-normal text-smtr-main-black-100 placeholder:text-smtr-main-tertiary-50 md:!text-xs"
            onChange={(nextValue) => {
                updateDraftValue(nextValue);

                if (DateHelpers.parseDisplayDate(nextValue, [DISPLAY_DATE_FORMAT])) {
                    onCommit(nextValue);
                }
            }}
            onBlur={(event) => {
                if (event.currentTarget.dataset.skipNextBlurSave === 'true') {
                    delete event.currentTarget.dataset.skipNextBlurSave;
                    return;
                }

                onCommit(draftValueRef.current);
            }}
            onKeyDown={(event) => {
                if (event.key === 'Enter') {
                    event.preventDefault();
                    onCommit(draftValueRef.current);
                    event.currentTarget.dataset.skipNextBlurSave = 'true';
                    event.currentTarget.blur();
                }

                if (event.key === 'Escape') {
                    event.preventDefault();
                    onCancel();
                    updateDraftValue(initialValue);
                    event.currentTarget.dataset.skipNextBlurSave = 'true';
                    event.currentTarget.blur();
                }
            }}
        />
    );
};
